import type { createPagePointer } from './page-pointer';

type Pointer = ReturnType<typeof createPagePointer>;

/** 已登记的目的地沿真实页签/菜单点击，不向模型请求导航，也不绕过控件直接改路由。 */
export async function navigateAgentPage(
  destination: string,
  options: {
    document: Document;
    pointer: Pointer;
    location: () => { pathname: string; search: string };
    active: () => boolean;
    track: (event: string, data: { reason?: string; source?: string }) => void;
    timeoutMs?: number;
  },
): Promise<boolean> {
  const { document: doc, pointer, location, track } = options;
  const target = new URL(destination, doc.baseURI);
  const deadline = Date.now() + (options.timeoutMs ?? 15000);
  let interrupted = false;
  const interrupt = () => {
    interrupted = true;
  };
  const active = () => !interrupted && options.active() && doc.visibilityState !== 'hidden';
  const usable = (element: HTMLElement) =>
    element.isConnected &&
    element.getClientRects().length > 0 &&
    !element.closest(
      '[hidden],[inert],[aria-hidden="true"],[data-agent-page-exclude],[data-agent-chat]',
    ) &&
    !element.matches(':disabled,[aria-disabled="true"]') &&
    doc.defaultView?.getComputedStyle(element).visibility !== 'hidden';
  const visible = (selector: string) =>
    Array.from(doc.querySelectorAll<HTMLElement>(selector)).filter(usable);
  const route = (element: HTMLElement) => {
    const base = element.closest<HTMLElement>('[data-agent-route-base]')?.dataset.agentRouteBase;
    const tabKey = element.closest<HTMLElement>('[data-node-key]')?.dataset.nodeKey;
    const value =
      element.dataset.agentRoute ||
      (base && tabKey ? `${base}/${tabKey}` : undefined) ||
      element.getAttribute('href');
    if (!value) return undefined;
    const rawUrl = new URL(value, doc.baseURI);
    const url = rawUrl.hash.startsWith('#/')
      ? new URL(rawUrl.hash.slice(1), rawUrl.origin)
      : rawUrl;
    return url.origin === target.origin ? url : undefined;
  };
  const clicked = new Set<string>();
  let revealed: HTMLElement | undefined;
  let revealedAt = 0;
  let clickedMenuRoot = false;
  let clickedSelectedTab = false;
  let waitingForPage = false;
  const click = async (element: HTMLElement, source: string) => {
    track('agent.navigation.click', { source });
    return pointer.port.point(element, () => {
      if (active()) element.click();
    });
  };
  doc.addEventListener('pointerdown', interrupt, true);
  doc.addEventListener('keydown', interrupt, true);
  try {
    while (active() && Date.now() < deadline) {
      if (location().pathname === target.pathname) {
        const workspaceTab = visible('[data-agent-workspace-tab]').find(
          (element) => route(element)?.pathname === target.pathname,
        );
        if (
          workspaceTab &&
          workspaceTab.getAttribute('aria-selected') !== 'true' &&
          !clickedSelectedTab
        ) {
          clickedSelectedTab = true;
          if (!(await click(workspaceTab, 'workspace_tab'))) return false;
        }
        if (
          workspaceTab &&
          (workspaceTab.getAttribute('aria-selected') !== 'true' ||
            !visible('[data-agent-workspace-page]').some(
              (element) =>
                element.dataset.agentWorkspacePage === target.pathname &&
                element.childElementCount > 0,
            ))
        ) {
          waitingForPage = true;
          await new Promise((resolve) => setTimeout(resolve, 50));
          continue;
        }
        waitingForPage = false;
        // URL 查询参数可能只用于初始选中；以可见控件的实际选择为准。
        let pending = false;
        for (const [parameter, value] of target.searchParams) {
          const groups = visible('[data-agent-tab-param]').filter(
            (element) => element.dataset.agentTabParam === parameter,
          );
          const tab = groups
            .flatMap((group) =>
              Array.from(
                group.querySelectorAll<HTMLElement>('[data-agent-tab-value],[data-node-key]'),
              )
                .filter(
                  (item) =>
                    item.closest('[data-agent-tab-param]') === group &&
                    (item.dataset.agentTabValue || item.dataset.nodeKey) === value,
                )
                .map((item) =>
                  item.matches('[role="tab"]')
                    ? item
                    : item.closest<HTMLElement>('[role="tab"]') ||
                      item.querySelector<HTMLElement>('[role="tab"]'),
                ),
            )
            .find((item): item is HTMLElement => Boolean(item && usable(item)));
          if (tab?.getAttribute('aria-selected') === 'true') continue;
          if (!groups.length && new URLSearchParams(location().search).get(parameter) === value)
            continue;
          pending = true;
          if (tab && !clicked.has(`${parameter}=${value}`)) {
            clicked.add(`${parameter}=${value}`);
            if (!(await click(tab, 'page_tab'))) return false;
          }
          break;
        }
        if (!pending) return true;
      } else {
        const entries = visible(
          '[data-agent-route],[data-agent-nav][href],[data-agent-route-base] [role="tab"]',
        ).filter((element) => element.getAttribute('target') !== '_blank');
        const exact = entries.filter((element) => route(element)?.pathname === target.pathname);
        const entry =
          exact.find((element) => element.hasAttribute('data-agent-workspace-tab')) || exact[0];
        if (entry && !clicked.has(target.pathname)) {
          clicked.add(target.pathname);
          if (
            !(await click(
              entry,
              entry.hasAttribute('data-agent-workspace-tab') ? 'workspace_tab' : 'menu',
            ))
          )
            return false;
        } else if (!entry) {
          // Some browser/Popover combinations do not open a menu from a synthetic hover.
          // Click that same visible owning entry, then use the now-visible submenu or page link.
          if (revealed && !clickedMenuRoot && Date.now() - revealedAt >= 700) {
            clickedMenuRoot = true;
            if (!(await click(revealed, 'menu_root'))) return false;
            await new Promise((resolve) => setTimeout(resolve, 50));
            continue;
          }
          const ancestor = entries
            .filter((element) => {
              const path = element.dataset.agentNavRoot || route(element)?.pathname;
              return (
                path && path !== '/' && target.pathname.startsWith(`${path}/`) && !clicked.has(path)
              );
            })
            .sort((a, b) => route(b)!.pathname.length - route(a)!.pathname.length)[0];
          if (ancestor) {
            const path = ancestor.dataset.agentNavRoot || route(ancestor)!.pathname;
            clicked.add(path);
            if (ancestor.hasAttribute('data-agent-nav-menu')) {
              revealed = ancestor;
              revealedAt = Date.now();
              if (
                !(await pointer.hover(ancestor, () => {
                  if (active())
                    ancestor.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
                }))
              )
                return false;
            } else if (!(await click(ancestor, 'menu'))) return false;
          }
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    track('agent.navigation.failed', {
      reason: !active() ? 'interrupted' : waitingForPage ? 'page_not_ready' : 'control_unavailable',
    });
    return false;
  } finally {
    revealed?.dispatchEvent(new MouseEvent('mouseout', { bubbles: true }));
    doc.removeEventListener('pointerdown', interrupt, true);
    doc.removeEventListener('keydown', interrupt, true);
  }
}
